import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2Icon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { spring } from "../../utils/motion";
export type ButtonVariant = "accent" | "brand" | "surface" | "ink" | "good" | "bad";
type ButtonSize = "sm" | "md" | "lg";
interface BrutalButtonProps {
  children: ReactNode;
  to?: string;
  href?: string;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
  disabled?: boolean;
  loading?: boolean;
  ariaLabel?: string;
  className?: string;
}
const faceColor: Record<ButtonVariant, string> = {
  accent: "bg-accent text-night",
  brand: "bg-brand text-white",
  surface: "bg-surface text-ink",
  ink: "bg-ink text-paper",
  good: "bg-good text-night",
  bad: "bg-bad text-night"
};
const sizing: Record<ButtonSize, {
  face: string;
  offset: number;
  radius: string;
}> = {
  sm: {
    face: "px-3.5 py-2 text-sm gap-1.5",
    offset: 2,
    radius: "rounded-xl"
  },
  md: {
    face: "px-5 py-3 text-[15px] gap-2",
    offset: 3,
    radius: "rounded-xl"
  },
  lg: {
    face: "px-7 py-3.5 text-lg gap-2.5",
    offset: 3,
    radius: "rounded-card"
  }
};
export function BrutalButton({
  children,
  to,
  href,
  onClick,
  type = "button",
  variant = "accent",
  size = "md",
  full = false,
  disabled = false,
  loading = false,
  ariaLabel,
  className
}: BrutalButtonProps) {
  const reduce = useReducedMotion() ?? false;
  const s = sizing[size];
  const inactive = disabled || loading;
  const face = {
    rest: {
      x: 0,
      y: 0
    },
    hover: reduce || inactive ? {
      x: 0,
      y: 0
    } : {
      x: -1,
      y: -1
    },
    press: inactive ? {
      x: 0,
      y: 0
    } : {
      x: s.offset,
      y: s.offset
    }
  };
  const body = <motion.span initial="rest" animate="rest" whileHover="hover" whileTap="press" className={`relative flex w-full ${s.radius}`} data-id="element-1183" data-mp="2jcmg6va6kkbe">
      <span aria-hidden="true" className={`absolute inset-0 ${s.radius} bg-line/85`} style={{
      transform: `translate(${s.offset}px, ${s.offset}px)`
    }} data-id="element-1184" data-mp="2f96kq29ukq0a" />
      <motion.span variants={face} transition={spring.snappy} className={twMerge(`relative flex w-full items-center justify-center whitespace-nowrap border-3 border-line font-semibold ${s.radius} ${s.face} ${faceColor[variant]}`, inactive && "opacity-60")} data-id="element-1185" data-mp="n8589l8m7wga">
        {loading && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1186" data-mp="3vjj5i6ql5i09" />}
        {children}
      </motion.span>
    </motion.span>;
  const shared = twMerge(`relative inline-flex ${s.radius} outline-none focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-4 focus-visible:outline-brand`, full && "w-full", inactive && "cursor-not-allowed", className);
  if (to && !inactive) {
    return <Link to={to} aria-label={ariaLabel} className={shared} data-id="element-1187" data-mp="1pn5h7hwh6v47">
        {body}
      </Link>;
  }
  if (href && !inactive) {
    return <a href={href} aria-label={ariaLabel} className={shared} data-id="element-1188" data-mp="3a8btmlivilo0">
        {body}
      </a>;
  }
  return <button type={type} onClick={onClick} disabled={inactive} aria-label={ariaLabel} aria-busy={loading || undefined} className={shared} data-id="element-1189" data-mp="359ij91yvvas2">
      {body}
    </button>;
}